import AnalyticsIcon from '@mui/icons-material/Analytics';
import DevicesOutlinedIcon from '@mui/icons-material/DevicesOutlined';
import GroupsIcon from '@mui/icons-material/Groups';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import PublicIcon from '@mui/icons-material/Public';
import SecurityIcon from '@mui/icons-material/Security';
import ShareIcon from '@mui/icons-material/Share';
import SpeedIcon from '@mui/icons-material/Speed';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import {
  Box,
  Card,
  CardContent,
  Container,
  Grid,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React from 'react';

const BenefitsSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(8, 0),
  background: theme.palette.background.default,
}));

const AnimatedCard = styled(Card)(({ theme }) => ({
  height: '100%',
  transition: 'all 0.3s ease-in-out',
  background: theme.palette.background.paper,
  borderRadius: theme.spacing(2),
  boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
  overflow: 'hidden',
  '&:hover': {
    transform: 'translateY(-10px)',
    boxShadow: '0 12px 30px rgba(0,0,0,0.15)',
    '& .benefit-icon': {
      transform: 'scale(1.1)',
    },
  },
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  width: 60,
  height: 60,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.primary.main} 100%)`,
  marginBottom: theme.spacing(2),
  transition: 'transform 0.3s ease-in-out',
  '& svg': {
    fontSize: 30,
    color: theme.palette.common.white,
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  transition: 'transform 0.3s, box-shadow 0.3s',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(0,0,0,0.12)',
  },
  padding: theme.spacing(2),
  borderRadius: theme.spacing(2),
}));

const benefits = [
  {
    icon: <DevicesOutlinedIcon />,
    title: 'Powerful Platform',
    description:
      'Easy-to-use campaign builder with intuitive interface and real-time preview',
  },
  {
    icon: <PublicIcon />,
    title: 'Global Reach',
    description: 'Connect with donors worldwide and accept multiple currencies',
  },
  {
    icon: <SecurityIcon />,
    title: 'Bank-Level Security',
    description:
      'Advanced encryption and secure payment processing for peace of mind',
  },
  {
    icon: <SupportAgentIcon />,
    title: '24/7 Support',
    description: 'Dedicated support team ready to help you succeed',
  },
  {
    icon: <ShareIcon />,
    title: 'Social Integration',
    description: 'Seamlessly share campaigns across all social media platforms',
  },
  {
    icon: <MenuBookIcon />,
    title: 'Resource Center',
    description: 'Access guides, templates, and fundraising best practices',
  },
  {
    icon: <SpeedIcon />,
    title: 'Quick Setup',
    description: 'Launch your campaign in minutes with our streamlined process',
  },
  {
    icon: <AnalyticsIcon />,
    title: 'Smart Analytics',
    description: 'Track performance with detailed insights and donor analytics',
  },
];

const Benefits = () => {
  return (
    <Box sx={{ py: 8, bgcolor: 'background.default' }}>
      <Container maxWidth="lg">
        <Typography align="center" gutterBottom variant="h2">
          Why Choose Us
        </Typography>
        <Grid container spacing={4}>
          {[
            {
              icon: <ShareIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
              title: 'Easy Sharing',
              description:
                'Share your campaign across all social media platforms',
            },
            {
              icon: (
                <MonetizationOnIcon
                  sx={{ fontSize: 50, color: 'primary.main' }}
                />
              ),
              title: 'Low Fees',
              description:
                'Keep more of your donations with our competitive fee structure',
            },
            {
              icon: <SpeedIcon sx={{ fontSize: 50, color: 'primary.main' }} />,
              title: 'Quick Setup',
              description:
                'Start your fundraising campaign in minutes, not days',
            },
          ].map((benefit, index) => (
            <Grid
              key={index}
              size={{
                md: 4,
                sm: 6,
                xs: 12
              }}>
              <StyledCard>
                <CardContent>
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'center',
                      alignItems: 'center',
                      mb: 3,
                      height: 80,
                      '& svg': {
                        transition: 'transform 0.3s ease-in-out',
                      },
                      '&:hover svg': {
                        transform: 'scale(1.1)',
                      },
                    }}
                  >
                    {benefit.icon}
                  </Box>
                  <Typography
                    align="center"
                    component="h3"
                    gutterBottom
                    sx={{
                      fontWeight: 600,
                      minHeight: '64px', // Ensure consistent height for titles
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                    variant="h6"
                  >
                    {benefit.title}
                  </Typography>
                  <Typography
                    align="center"
                    color="text.secondary"
                    sx={{
                      minHeight: '60px', // Ensure consistent height for descriptions
                    }}
                    variant="body2"
                  >
                    {benefit.description}
                  </Typography>
                </CardContent>
              </StyledCard>
            </Grid>
          ))}
        </Grid>
      </Container>
    </Box>
  );
};

export default Benefits;
